<template>
  <IndexView />
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Detail Cash Register" />
      <div
        class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500">
        <div v-if="loading" class="flex justify-center items-center h-32">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
        <div v-else>
          <TableComponent>
            <TableHead>
              <TableRow>
                <TableHeadCell>No</TableHeadCell>
                <TableHeadCell>No. Order</TableHeadCell>
                <TableHeadCell>Note</TableHeadCell>
                <TableHeadCell>Total</TableHeadCell>
                <TableHeadCell>Tax</TableHeadCell>
                <TableHeadCell>Grand Total</TableHeadCell>
                <TableHeadCell>Pembayaran</TableHeadCell>
                <TableHeadCell>Channel</TableHeadCell>
                <TableHeadCell>Status</TableHeadCell>
                <TableHeadCell>View</TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow v-for="(order, index) in salesOrders" :key="order.id">
                <TableCell>{{ index + 1 }}</TableCell>
                <TableCell>
                  <div class="flex flex-col">
                    <p>{{ order.code }}</p>
                    <p>{{ formatTime(order.createdAt) }}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div class="flex flex-col">
                    <p>Customer: {{ order.customer_name }}</p>
                    <p>Staff: {{ getMarketingName(order.marketing_id) }}</p>
                  </div>
                </TableCell>
                <TableCell>{{ formatRupiah(order.sub_total) }}</TableCell>
                <TableCell>
                  <div class="flex flex-col text-right">
                    <p>Service: {{ formatRupiah(order.service_charge) }}</p>
                    <p>PPn: {{ formatRupiah(order.tax) }}</p>
                  </div>
                </TableCell>
                <TableCell>{{ formatRupiah(order.grand_total) }}</TableCell>
                <TableCell>{{ formatRupiah(order.payment_amount) }}</TableCell>
                <TableCell>{{ getPaymentMethodName(order.payment_method_id) }}</TableCell>
                <TableCell>
                  <span :class="{
                    'bg-success dark:bg-success text-light-50 dark:text-white':
                      order.status === 'paid',
                    'bg-warning dark:bg-warning text-light-50 dark:text-white':
                      order.status === 'pending',
                    'bg-danger dark:bg-danger text-light-50 dark:text-white': order.status === 'canceled',
                  }" class="px-2 py-1 rounded-full text-xs font-medium uppercase">
                    {{ order.status }}
                  </span>
                </TableCell>
                <TableCell>
                  <button :id="'view-order-' + order.id"
                    class="bg-info hover:bg-hover-info flex justify-center items-center mx-1 p-2 rounded-md group"
                    @click="viewOrder(order.id)">
                    <EyeIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                  </button>
                </TableCell>
              </TableRow>
            </TableBody>
          </TableComponent>
          <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
  import IndexView from "@/views/IndexView.vue";
  import PageTitle from "@/components/PageTitle.vue";
  import TableComponent from "@/components/table/Table.vue";
  import TableHead from "@/components/table/TableHead.vue";
  import TableBody from "@/components/table/TableBody.vue";
  import TableRow from "@/components/table/TableRow.vue";
  import TableCell from "@/components/table/TableCell.vue";
  import TableHeadCell from "@/components/table/TableHeadCell.vue";
  import { EyeIcon } from "@heroicons/vue/24/outline";
  import { formatTime } from "@/helpers/formatTime";
  import { formatRupiah } from "@/helpers/formatRupiah";
  import { mapGetters } from "vuex";
  import ViewContainer from "@/components/ViewContainer.vue";
  import PaginationButtons from "@/components/PaginationButtons.vue";
import { paymentMethode } from "@/helpers/constanta";

  export default {
    name: "CashRegisterDetailView",
    components: {
      IndexView,
      ViewContainer,
      PageTitle,
      TableComponent,
      TableHead,
      TableHeadCell,
      TableBody,
      TableRow,
      TableCell,
      EyeIcon,
      PaginationButtons,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        paymentMethode,
        loading: true,
        salesOrders: [],
        currentPage: 1,
        totalPages: 1,
      };
    },
    computed: {
      ...mapGetters("sales_orders", ["allSalesOrders"]),
      ...mapGetters("tables", ["allTables"]),
      ...mapGetters("marketings", ["allMarketings"]),
      ...mapGetters("payment_methods", ["allPaymentMethods"]),

      getTableName() {
        return (tableId) => {
          const table = this.allTables.find((t) => t.id === tableId);
          return table ? table.name : tableId;
        };
      },
      getMarketingName() {
        return (marketingId) => {
          const marketing = this.allMarketings.find((m) => m.id === marketingId);
          return marketing ? marketing.name : marketingId;
        };
      },
      getPaymentMethodName() {
        return (paymentMethodId) => {
          const paymentMethod = this.allPaymentMethods.find((m) => m.id === paymentMethodId);
          return paymentMethod ? (paymentMethode?.[paymentMethod?.name?.toUpperCase()] || paymentMethod.name) : paymentMethodId;
        };
      },
    },
    async created() {
      this.fetchSalesOrders();
      this.fetchPaymentMethods();
      this.fetchTables();
      this.fetchMarketings();
    },
    watch: {
      currentPage: {
        immediate: true,
        handler(newVal) {
          this.fetchSalesOrders({ page: newVal });
        },
      },
    },
    methods: {
      formatTime,
      formatRupiah,
      async fetchPaymentMethods() {
        this.$store.dispatch('payment_methods/fetchPaymentMethods', { limit: 1000 });
      },
      async fetchTables() {
        this.$store.dispatch('tables/fetchTables', { limit: 1000 });
      },
      async fetchMarketings() {
        this.$store.dispatch('marketings/fetchMarketings', { limit: 1000 });
      },
      async fetchSalesOrders(params = {}) {
        if (!params.page) params.page = this.currentPage;
        if (!params.limit) params.limit = 50;
        try {
          const { data, pagination } = await this.$store.dispatch(
            "sales_orders/fetchSalesOrders",
            {
              ...params,
              cash_register_id: this.id,
            }
          );
          this.salesOrders = data;
          if (pagination) {
            this.totalPages = pagination.totalPages;
          }
        } catch (error) {
          console.error("Error fetching sales orders:", error);
        } finally {
          this.loading = false;
        }
      },
      viewOrder(orderId) {
        this.$router.push({
          path: "/payment",
          query: { orderId: orderId.toString() },
        });
      },
    },
  };
</script>
